import type { SelectOption } from '@aus-platform/design-system';
import { ColorCodes } from '@aus-platform/design-system';
import { useEffect, useState } from 'react';
import { handleResponseErrorMessage, useCRSList } from '../api';
import { type CrsType, WGS84Crs } from '../enums';

export const useCRS = (sourceCrs?: string, crsType?: CrsType[]) => {
  // useStates
  const [crsSelected, setCrsSelected] = useState<SelectOption | null>(null);
  const [crsSelectOptions, setCrsSelectOptions] = useState<SelectOption[]>([]);

  // Apis.
  const {
    data: crsListResponse,
    isSuccess: isSuccessCrsList,
    isError: isErrorCrsList,
    error: crsListError,
    isLoading: isLoadingCrsList,
  } = useCRSList(crsType);

  // useEffects.
  useEffect(() => {
    if (isErrorCrsList && crsListError) {
      handleResponseErrorMessage(isErrorCrsList, crsListError);
    }
  }, [isErrorCrsList, crsListError]);

  useEffect(() => {
    if (crsListResponse && isSuccessCrsList) {
      const { crs } = crsListResponse;
      const filteredCrsList = crs.filter(
        (eachCrs) =>
          eachCrs.epsgCode !== WGS84Crs.epsgCode &&
          eachCrs.epsgCode !== sourceCrs,
      );

      const selectedCrs = sourceCrs
        ? crs.find((eachCrs) => eachCrs.epsgCode === sourceCrs)!
        : WGS84Crs;
      setCrsSelected({
        label: `EPSG:${selectedCrs.epsgCode} / ${selectedCrs.name}`,
        value: selectedCrs.epsgCode,
      });

      const finalCrsList =
        sourceCrs && sourceCrs !== WGS84Crs.epsgCode
          ? [selectedCrs, WGS84Crs, ...filteredCrsList]
          : [WGS84Crs, ...filteredCrsList];

      setCrsSelectOptions(
        finalCrsList.map((crsEach) => ({
          label: `EPSG:${crsEach.epsgCode} / ${crsEach.name}`,
          value: crsEach.epsgCode,
        })),
      );
    }
  }, [crsListResponse, isSuccessCrsList]);

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      borderBottom:
        state.data.value === WGS84Crs.epsgCode
          ? `1px solid ${ColorCodes.Neutral50}`
          : '',
      backgroundColor: state.isSelected
        ? `${ColorCodes.Primary500}`
        : state.isFocused
          ? `${ColorCodes.Neutral50}`
          : '',
    }),
  };

  return {
    crsSelected,
    crsSelectOptions,
    customStyles,
    isLoadingCrsList,
  };
};
